import { Icon } from '@iconify/react'
import { hasLandingSocialLinks, type CampaignSocialLinks } from '@/lib/campaign-social-links'
import { LandingFooterSocialLinks } from '@/components/Landing/LandingFooterSocialLinks/LandingFooterSocialLinks'
import type { LandingInstitution } from '@/types/landing'
import { PUBLIC_LEGAL_LINKS } from '@/lib/public-legal-links'
import styles from './LandingDefaultFooter.module.css'

const PAYMENT_METHODS = ['Pix'] as const

type LandingDefaultFooterProps = {
  campaignTitle: string
  institution: LandingInstitution
  institutionName: string
  institutionAvatarUrl: string | null
  socialLinks: CampaignSocialLinks
  contactWhatsappHref: string
  onAccountAction: () => void
}

function formatInstitutionAddress(institution: LandingInstitution): string | null {
  const address = institution.address
  if (!address) return null

  const streetLine = [address.street, address.number].filter(Boolean).join(', ')
  const cityLine = [address.neighborhood, address.city, address.state].filter(Boolean).join(' · ')
  const parts = [streetLine, cityLine].filter((part) => part.trim().length > 0)

  return parts.length > 0 ? parts.join(' — ') : null
}

type FooterInfoRowProps = {
  icon: string
  label: string
  value: string
  href?: string
}

function FooterInfoRow({ icon, label, value, href }: FooterInfoRowProps) {
  const content = (
    <>
      <span className={styles.infoIcon} aria-hidden>
        <Icon icon={icon} />
      </span>
      <div className={styles.infoCopy}>
        <span className={styles.infoLabel}>{label}</span>
        <span className={styles.infoValue}>{value}</span>
      </div>
    </>
  )

  if (href) {
    return (
      <a href={href} className={styles.infoRow} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    )
  }

  return <div className={styles.infoRow}>{content}</div>
}

export function LandingDefaultFooter({
  campaignTitle,
  institution,
  institutionName,
  institutionAvatarUrl,
  socialLinks,
  contactWhatsappHref,
  onAccountAction,
}: LandingDefaultFooterProps) {
  const institutionAddress = formatInstitutionAddress(institution)
  const hasSocialLinks = hasLandingSocialLinks(socialLinks)
  const mailHref = institution.email ? `mailto:${institution.email}` : undefined
  const phoneHref = institution.phone ? `tel:${institution.phone.replace(/\D/g, '')}` : undefined

  return (
    <footer className={styles.footer} id="landing-footer">
      <div className={styles.footerGlow} aria-hidden />

      <div className={styles.footerInner}>
        <div className={styles.footerHero}>
          <div className={styles.footerBrand}>
            {institutionAvatarUrl ? (
              <img
                src={institutionAvatarUrl}
                alt=""
                className={styles.footerBrandLogo}
                loading="lazy"
              />
            ) : (
              <div className={styles.footerBrandFallback} aria-hidden>
                {institutionName.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className={styles.footerBrandCopy}>
              <span className={styles.footerEyebrow}>Campanha oficial</span>
              <strong className={styles.footerBrandTitle}>{campaignTitle}</strong>
              <span className={styles.footerBrandSubtitle}>{institutionName}</span>
            </div>
          </div>

          <div className={styles.footerHeroActions}>
            <button type="button" className={styles.footerCtaPrimary} onClick={onAccountAction}>
              <Icon icon="mdi:account-circle-outline" aria-hidden />
              Minha Conta
            </button>
            <a
              href={contactWhatsappHref}
              className={styles.footerCtaSecondary}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="mdi:whatsapp" aria-hidden />
              Contato
            </a>
          </div>
        </div>

        <div className={styles.footerGrid}>
          <section className={styles.footerCard} aria-labelledby="footer-responsible-title">
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon} aria-hidden>
                <Icon icon="mdi:shield-check-outline" />
              </span>
              <h2 id="footer-responsible-title">Responsável pelo sorteio</h2>
            </div>

            <div className={styles.infoList}>
              <FooterInfoRow icon="mdi:domain" label="Empresa" value={institutionName} />
              {institution.document_cnpj ? (
                <FooterInfoRow icon="mdi:file-document-outline" label="CNPJ" value={institution.document_cnpj} />
              ) : null}
              {institution.email ? (
                <FooterInfoRow icon="mdi:email-outline" label="E-mail" value={institution.email} href={mailHref} />
              ) : null}
              {institution.phone ? (
                <FooterInfoRow icon="mdi:phone-outline" label="Telefone" value={institution.phone} href={phoneHref} />
              ) : null}
              {institutionAddress ? (
                <FooterInfoRow icon="mdi:map-marker-outline" label="Endereço" value={institutionAddress} />
              ) : null}
            </div>
          </section>

          {hasSocialLinks ? (
            <section className={styles.footerCard} aria-labelledby="footer-social-title">
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon} aria-hidden>
                  <Icon icon="mdi:share-variant-outline" />
                </span>
                <h2 id="footer-social-title">Siga-nos</h2>
              </div>
              <p className={styles.cardDescription}>Acompanhe novidades e resultados nas redes oficiais.</p>
              <LandingFooterSocialLinks
                links={socialLinks}
                linkClassName={styles.footerSocialLink}
              />
            </section>
          ) : null}

          <section className={styles.footerCard} aria-labelledby="footer-info-title">
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon} aria-hidden>
                <Icon icon="mdi:information-outline" />
              </span>
              <h2 id="footer-info-title">Informações legais</h2>
            </div>

            <div className={styles.legalLinks}>
              <a href={PUBLIC_LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
                <Icon icon="mdi:shield-lock-outline" aria-hidden />
                Política de Privacidade
              </a>
              <a href={PUBLIC_LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className={styles.legalLink}>
                <Icon icon="mdi:file-sign" aria-hidden />
                Termos e Condições de Uso
              </a>
            </div>

            <div className={styles.trustBadges}>
              <span className={styles.trustBadge}>
                <Icon icon="mdi:lock-check-outline" aria-hidden />
                Compra segura
              </span>
              <span className={styles.trustBadge}>
                <Icon icon="mdi:flash-outline" aria-hidden />
                Confirmação rápida
              </span>
            </div>
          </section>
        </div>

        <section className={styles.paymentSection} aria-labelledby="footer-payments-title">
          <div className={styles.paymentHeader}>
            <span className={styles.cardIcon} aria-hidden>
              <Icon icon="mdi:credit-card-outline" />
            </span>
            <h2 id="footer-payments-title">Formas de pagamento</h2>
          </div>
          <div className={styles.paymentGrid}>
            {PAYMENT_METHODS.map((method) => (
              <span key={method} className={styles.paymentBadge}>
                <Icon icon="simple-icons:pix" className={styles.paymentPixIcon} aria-hidden />
                {method}
              </span>
            ))}
          </div>
        </section>

        <div className={styles.footerBottom}>
          <span>© 2026 SORTEIOS.COM.BR. Todos os direitos reservados.</span>
        </div>
      </div>
    </footer>
  )
}
